import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, Eye, ArrowRight, Search, Tag, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useTranslations } from '../context/TranslationsContext';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_KEYS_FULL = ['january','february','march','april','may_full','june','july','august','september','october','november','december'];

// Skeleton loader for posts
const PostSkeleton = () => (
  <div className="animate-pulse">
    <div className="bg-white/10 rounded-xl aspect-video mb-4" />
    <div className="h-4 bg-white/10 rounded w-1/4 mb-2" />
    <div className="h-6 bg-white/10 rounded w-3/4 mb-2" />
    <div className="h-4 bg-white/10 rounded w-full mb-2" />
    <div className="h-4 bg-white/10 rounded w-2/3" />
  </div>
);

const BlogPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td: tCms } = useTranslations(['blog', 'common']);
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = i18n.language;
  
  // Translation helper
  const t = (key, fallback) => {
    const cmsValue = tCms(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    const i18nVal = ti18n(key);
    if (i18nVal && i18nVal !== key) return i18nVal;
    return fallback || key;
  };
  
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const currentSort = searchParams.get('sort') || 'featured';

  useEffect(() => {
    fetchData();
  }, [category, currentPage, currentSort]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [postsRes, catsRes] = await Promise.all([
        axios.get(`${API}/api/blog/posts`, {
          params: {
            page: currentPage,
            limit: 12,
            category: category || undefined,
            sort: currentSort
          }
        }),
        axios.get(`${API}/api/blog/categories`)
      ]);
      
      setPosts(postsRes.data.posts);
      setPagination(postsRes.data.pagination);
      setCategories(catsRes.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (sort) => {
    setSearchParams({ sort, page: '1' });
  };

  const handlePageChange = (page) => {
    setSearchParams({ sort: currentSort, page: page.toString() });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const monthKey = MONTH_KEYS_FULL[date.getMonth()];
    const monthName = tCms(`months.${monthKey}`) || date.toLocaleDateString('pl-PL', { month: 'long' });
    return `${day} ${monthName} ${date.getFullYear()}`;
  };

  const currentCategory = category 
    ? categories.find(c => c.slug === category)
    : null;

  return (
    <div className="page-gradient min-h-screen pt-28 sm:pt-32 pb-20 px-4 sm:px-6 md:px-12">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {currentCategory 
              ? (typeof currentCategory.name === 'object' 
                  ? (currentCategory.name[lang] || currentCategory.name.pl || Object.values(currentCategory.name)[0])
                  : currentCategory.name)
              : t('blog.title', 'Blog')}
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {typeof currentCategory?.description === 'object'
              ? (currentCategory.description[lang] || currentCategory.description.pl || '')
              : (currentCategory?.description || t('blog.description', 'Aktualności, porady i wydarzenia z AvePark'))}
          </p>
        </motion.div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          <Link to={`/${lang}/blog`}>
            <Button 
              variant={!category ? 'default' : 'outline'}
              size="sm"
              className={!category ? 'bg-primary' : 'border-white/20 text-white hover:bg-white/10'}
            >
              {t('blog.allPosts', 'Wszystkie')}
            </Button>
          </Link>
          {categories.map(cat => (
            <Link key={cat.slug} to={`/${lang}/blog/kategoria/${cat.slug}`}>
              <Button
                variant={category === cat.slug ? 'default' : 'outline'}
                size="sm"
                className={category === cat.slug ? 'bg-primary' : 'border-white/20 text-white hover:bg-white/10'}
              >
                <Tag className="w-3 h-3 mr-1" />
                {typeof cat.name === 'object' ? (cat.name[lang] || cat.name.pl || Object.values(cat.name)[0]) : cat.name}
              </Button>
            </Link>
          ))}
        </div>

        {/* Sort options */}
        <div className="flex justify-center gap-2 mb-8">
          {[
            { value: 'featured', label: t('blog.featured', 'Polecane') },
            { value: 'newest', label: t('blog.latest', 'Najnowsze') },
            { value: 'popular', label: t('blog.popular', 'Popularne') }
          ].map(option => (
            <Button
              key={option.value}
              variant="ghost"
              size="sm"
              onClick={() => handleSortChange(option.value)}
              className={currentSort === option.value 
                ? 'text-primary border-b-2 border-primary rounded-none' 
                : 'text-gray-400 hover:text-white'}
            >
              {option.label}
            </Button>
          ))}
        </div>

        {/* Posts grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <PostSkeleton key={i} />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">
              {t('blog.noPosts', 'Brak artykułów w tej kategorii')}
            </p>
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/${lang}/blog/${post.slug}`} data-testid={`blog-post-${post.slug}`}>
                  <Card className="bg-white/5 border-white/10 overflow-hidden hover:border-primary/50 transition-all duration-300 group h-full">
                    {/* Image */}
                    <div className="aspect-video overflow-hidden bg-white/5">
                      {post.featuredImage ? (
                        <img
                          src={post.featuredImage}
                          alt={typeof post.title === 'object' ? (post.title[lang] || post.title.pl || '') : post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-purple-600/20">
                          <span className="text-4xl font-bold text-white/20">
                            {(typeof post.title === 'object' ? (post.title[lang] || post.title.pl || 'A') : (post.title || 'A'))[0]}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <CardContent className="p-5">
                      {/* Category */}
                      {post.category && (
                        <span className="text-xs text-primary font-medium uppercase tracking-wider">
                          {typeof post.category.name === 'object' 
                            ? (post.category.name[lang] || post.category.name.pl || Object.values(post.category.name)[0])
                            : post.category.name}
                        </span>
                      )}
                      
                      {/* Title */}
                      <h3 className="text-lg font-semibold text-white mt-2 mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {typeof post.title === 'object'
                          ? (post.title[lang] || post.title.pl || Object.values(post.title)[0])
                          : post.title}
                      </h3>
                      
                      {/* Excerpt */}
                      <p className="text-gray-400 text-sm line-clamp-2 mb-4">
                        {typeof post.excerpt === 'object'
                          ? (post.excerpt[lang] || post.excerpt.pl || '')
                          : (post.excerpt || '')}
                      </p>
                      
                      {/* Meta */}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.publishedAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {post.viewCount || 0}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="flex justify-center gap-2 mt-12">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="border-white/20"
            >
              {t('common.previous', 'Poprzednia')}
            </Button>
            
            {Array.from({ length: pagination.pages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === pagination.pages || Math.abs(p - currentPage) <= 1)
              .map((page, index, arr) => (
                <React.Fragment key={page}>
                  {index > 0 && arr[index - 1] !== page - 1 && (
                    <span className="text-gray-500 px-2">...</span>
                  )}
                  <Button
                    variant={page === currentPage ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePageChange(page)}
                    className={page !== currentPage ? 'border-white/20' : ''}
                  >
                    {page}
                  </Button>
                </React.Fragment>
              ))}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.pages}
              className="border-white/20"
            >
              {t('common.next', 'Następna')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
